import { parse as parsePath } from 'path';
import cookie from 'cookie';
import { randomBytes } from 'crypto';

import { Context } from './context.js';
import { CartItemError, Decimal, FormError, Identified, IdOf, Index, Maybe, None, Some, UserError } from './data.js';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear.js';
import minMax from 'dayjs/plugin/minMax.js';
import { rgbToHex, toRGB } from './data/colors.js';
import { CurrencyId } from './data/countries.js';
import { getContentUrlPrefix } from './data/images.js';

// @ts-ignore
import * as url from 'native-url';
import { UrlWithParsedQuery } from 'node:url';

dayjs.extend(quarterOfYear);
dayjs.extend(minMax);

// eslint-disable-next-line
const utilError = (fn: Function, message?: string, options?: ErrorOptions) => {
	const err = new Error(message, options);
	// eslint-disable-next-line
	Error.captureStackTrace?.(err, fn);
	return err;
};

interface TupleOf<T> {
	0: [];
	1: [T];
	2: [T, T];
	3: [T, T, T];
	4: [T, T, T, T];
	5: [T, T, T, T, T];
	6: [T, T, T, T, T, T];
	7: [T, T, T, T, T, T, T];
	8: [T, T, T, T, T, T, T, T];
	9: [T, T, T, T, T, T, T, T, T];
	10: [T, T, T, T, T, T, T, T, T, T];
	11: [T, T, T, T, T, T, T, T, T, T, T];
	12: [T, T, T, T, T, T, T, T, T, T, T, T];
}

export type AnyTupleSize = keyof TupleOf<any>;

export type Tuple<T, N extends AnyTupleSize> = TupleOf<T>[N];
export type AtLeastN<T, N extends AnyTupleSize> = [...TupleOf<T>[N], ...T[]];
export type NonEmptyList<T> = [T, ...T[]];

export type Pair<T> = [T, T];
export type Triple<T> = [T, T, T];
export type Four<T> = [T, T, T, T];
export type Five<T> = [T, T, T, T, T];

export type TupleSize<U> = keyof { [N in AnyTupleSize as (U extends Tuple<any, N> ? N : never)]: N };
export type MapTuple<U, R> = Tuple<R, TupleSize<U>>;

export type Awaitable<T> = T | Promise<T>;

export function notEmpty<T>(arr: Maybe<T[]>): arr is [T, ...T[]];
export function notEmpty(str: Maybe<string>): str is Exclude<string, ''>;
export function notEmpty(val: Maybe<any[] | string>): val is Exclude<any[] | string, '' | []>;
export function notEmpty(val: Maybe<any[] | string>) {
	return isSome(val) && val.length > 0;
}

export function assertNotEmpty<T>(arr: Maybe<T[]>): asserts arr is [T, ...T[]];
export function assertNotEmpty(str: Maybe<string>): asserts str is Exclude<string, ''>;
export function assertNotEmpty(val: Maybe<any[] | string>) {
	if (isNone(val) || !val.length) throw utilError(assertNotEmpty, `Unexpected empty ${typeof val == 'string' ? 'string' : 'array'}`);
}

export function isEmpty(arr: Maybe<any[]>): arr is [];
export function isEmpty(str: Maybe<string>): str is '';
export function isEmpty(val: Maybe<any[] | string>) {
	return isSome(val) && !val.length;
}

export function isEmptyOrNone(arr: Maybe<any[]>): arr is Maybe<[]>;
export function isEmptyOrNone(str: Maybe<string>): str is Maybe<''>;
export function isEmptyOrNone(val: Maybe<string | any[]>): val is Maybe<'' | []>;
export function isEmptyOrNone(val: Maybe<any[] | string>) {
	return isNone(val) || !val.length;
}

export function objectEmpty(obj: Maybe<object>): obj is { [key: keyof any]: never } {
	return !Object.keys(ensure(obj)).length;
}
export function objectEmptyOrNone(obj: Maybe<object>): obj is Maybe<{ [key: keyof any]: never }> {
	return isNone(obj) || !Object.keys(obj).length;
}
export function objectNotEmpty<T extends Maybe<object>>(obj: T): obj is Some<T> {
	return isSome(obj) && !!Object.keys(obj).length;
}

export function lengthEq<T, const N extends keyof TupleOf<T>>(arr: Maybe<T[]>, n: N): arr is TupleOf<T>[N] {
	return isSome(arr) && arr.length == n;
}
export function ensureLength<T, const N extends keyof TupleOf<T>>(arr: T[], n: N): Tuple<T, N> {
	if (!lengthEq(arr, n)) throw utilError(ensureLength, `Expected tuple of length ${n}, got ${arr.length}`);
	return arr;
}

export function tuple<const T extends any[]>(...els: T) {
	return els;
}

export function mapTuple<const U extends any[], R>(tuple: U, fn: (it: U[number], i: number) => R) {
	return tuple.map(fn) as MapTuple<U, R>;
}

/**
 * Alternative to "as const" that doesn't actually mark the type readonly.
 * @param it
 */
export function lit<const T>(it: T) {
	return it as DeepMutable<T>;
}

export type EventParam<T extends Event = Event> = Omit<T, 'new'>;

export function range(size: number) {
	return [...Array(size).keys()];
}

export function makeSum<T>(defaultKey: keyof T, roundToPlaces = 2) {
	return (items: T[], key: keyof T = defaultKey) => num(
		
		items.reduce((a, it) => a + num(it[key] as any ?? 0, roundToPlaces), 0),
		roundToPlaces,
	);
}

export function keepExisting<T>(list: T[]) {
	return list.filter(it => it !== null && it !== undefined) as NonNullable<T>[];
}

export function unique<const T>(...lists: T[][]): T[] {
	return [...new Set(lists.flat(1))];
}

export interface ObjectPropertyPatch<T> {
	set?: T;
	delete?: boolean;
	length?: number;
	diff?: ObjectPatch<T>;
}

export type ObjectPatch<T> = {
	[K in keyof T]?: ObjectPropertyPatch<T[K]>
};

export function diffObjects<T extends {}>(a: T, b: T): ObjectPatch<T> {
	const acc: any = {};
	for (const key of new Set([...Object.keys(a), ...Object.keys(b)] as (keyof T)[]).values()) {
		const av = a[key], bv = b[key];
		const at = typeof av, bt = typeof bv;
		if (!hasOwnProperty(b, key)) {
			acc[key] = { delete: true };
		}
		else if (
			isSome(av) != isSome(bv)
			|| at != bt
			|| (at != 'object' && av !== bv)
			|| Array.isArray(av) != Array.isArray(bv)
		) {
			acc[key] = { set: bv ?? null };
		}
		else if (isSome(av) && isSome(bv) && at == 'object') {
			const diff = diffObjects(av, bv);
			if (!Object.keys(diff).length) continue;
			acc[key] = (
				Object.keys(diff).length >= Object.keys(bv).length * 0.5
					? { set: bv }
					: Array.isArray(bv) ? { length: (bv as any[]).length, diff } : { diff }
			);
		}
	}
	return acc;
}

export function patchObject<T>(target: T, patch: ObjectPatch<T>): T {
	
	const acc = structuredClone(target);
	
	const apply = <C>(to: C, diff: ObjectPatch<C>) => {
		for (const [key, action] of Object.entries(diff) as [keyof C, ObjectPropertyPatch<any>][]) {
			if (hasOwnProperty(action, 'set')) to[key] = action.set;
			if (action.delete) delete to[key];
			if (isSome(action.length)) (to[key] as any as any[]).length = action.length;
			if (action.diff) apply(to[key], action.diff);
		}
	};
	apply(acc, patch);
	
	return acc;
	
}

export function throttleSubsequent<A extends any[]>(fun: (...args: A) => void, time = 100) {
	let curArgs: A;
	let timeout: any;
	let firstRun = true;
	const call = function () {
		timeout = undefined;
		fun(...curArgs);
	};
	return function (...args: A) {
		curArgs = args;
		if (firstRun) {
			timeout = setTimeout(() => timeout = undefined, time);
			firstRun = false;
			fun(...args);
		}
		else {
			if (timeout) return;
			timeout = setTimeout(call, time);
		}
	};
}

export function throttle<A extends any[]>(fun: (...args: A) => void, time = 100) {
	let curArgs: A;
	let timeout: any;
	const call = function () {
		timeout = undefined;
		fun(...curArgs);
	};
	return function (...args: A) {
		curArgs = args;
		if (timeout) return;
		timeout = setTimeout(call, time);
	};
}

export function debounce<A extends any[]>(fun: (...args: A) => void, time = 100) {
	let curArgs: A;
	let timeout: any;
	const call = function () {
		timeout = undefined;
		fun(...curArgs);
	};
	return function (...args: A) {
		curArgs = args;
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(call, time);
	};
}

export function camelToKebab(s: string) {
	const fs = s[0]?.match(/[A-Z]/) ? s[0].toLowerCase() + s.slice(1) : s;
	return fs.replace(/[A-Z]/g, c => '-' + c.toLowerCase());
}

export function kebabToCamel(s: string) {
	return s.split(/-/g).map((w, i) => i ? capitalize(w) : w).join('');
}

export function capitalize<T extends string>(str: T) {
	return ((str[0] || '').toUpperCase() + str.slice(1)) as Capitalize<T>;
}

const dontCapitalize = ['a', 'the', 'on', 'of', 'in', 'with', 'is'];
export function capitalizeTitle(str: string) {
	return str.split(/[\s_]+/g).map(
		(w, i) => !!i && isIn(w, dontCapitalize) ? w : capitalize(w)
	).join(' ');
}

export function escapeHtml(text: string) {
	return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

export function makeInitials(name: string, length = 3) {
	const split = name.split(' ');
	if (lengthEq(split, 1)) return name.slice(0, length);
	return split.slice(0, length).map(s => s[0]).join('');
}

export function idToColor(id: string, s = 80, l = 40) {
	let acc = 0;
	for (let i = 0; i < id.length; i++) acc = acc * 36 + Math.max(0, base36.indexOf(id[i]!.toLowerCase()));
	return rgbToHex(toRGB([acc % 360, s, l]));
}

export function matchRoute(parts: string[], match: string[]): string[] | false {
	
	const ridx = match.indexOf('**');
	const mlen = ridx >= 0 ? ridx : match.length;
	if (mlen > parts.length) return false;
	
	const args: string[] = [];
	let i = 0;
	for (; i < parts.length; i++) {
		
		if (match.length <= i) return false;
		
		const mpart = match[i]!;
		
		if (mpart == '**') {
			args.push(parts.slice(i).join('/'));
			return args;
		}
		else if (mpart == '*') {
			args.push(parts[i]!);
		}
		else if (mpart != parts[i]) {
			if (mpart.includes('!*') && mpart.replace('!*', '') == parts[i]!.replace(/!\w+/, '')) {
				continue;
			}
			return false;
		}
		
	}
	
	if (i == parts.length) return args;
	
	return false;
	
}

export function setCookie(key: string, value: any, age?: Maybe<number>) {
	const params: cookie.CookieSerializeOptions = { path: '/' };
	params.maxAge = age || undefined;
	document.cookie = cookie.serialize(key, value, params);
}

type QueueEntry<R, P extends Promise<R>, A extends any[]> = {
	promise: P;
	args: A;
	resolve: (value: R | PromiseLike<R>) => void;
	reject: (reason?: any) => void;
};

export function queue<R, P extends Promise<R>, A extends any[]>(
	fun: (...args: A) => P,
	until = (res: R) => res === queue.done,
) {
	
	type QE = QueueEntry<R, P, A>;
	
	let q: QE[] | null = null;
	let done = false;
	let result: R | undefined;
	
	function run(qe: QE) {
		if (done) return qe.resolve(result!);
		try {
			fun(...qe.args).then(qe.resolve, qe.reject);
		}
		catch (e) {
			qe.reject(e);
		}
	}
	
	function next() {
		if (q && q.length) {
			const qe = q.shift()!;
			qe.promise.then(r => {
				if (!done && until(r)) {
					done = true;
					result = r;
				}
				next();
			}, e => {
				console.error(e);
				next();
			});
			run(qe);
		}
		else {
			q = null;
		}
	}
	
	return (...args: A) => {
		
		const start = !q;
		if (!q) q = [];
		
		const qe: Partial<QE> = { args };
		const p = qe.promise = new Promise((resolve, reject) => {
			qe.resolve = resolve;
			qe.reject = reject;
		}) as P;
		q.push(qe as QE);
		
		if (start) next();
		
		return p;
		
	};
	
}

queue.done = {};

export async function mapParallel<T, R>(list: T[], fn: (it: T, i: number) => Promise<R>, limit = 10): Promise<R[]> {
	const active = new Set<Promise<R>>();
	const results: R[] = Array(list.length);
	for (const [i, it] of list.entries()) {
		while (active.size >= limit) await Promise.race([...active]);
		const p = fn(it, i);
		active.add(p);
		void p.then(res => {
			results[i] = res;
			active.delete(p);
		}).catch(() => { /* do nothing, it will be rethrown by .race or .all since rejected promise stays in set */ });
	}
	await Promise.all([...active]);
	return results;
}

export const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

export function resourceLink(c: Pick<Context, 'resourceHashes'>, rfn: string) {
	const pp = parsePath(rfn);
	return `${getContentUrlPrefix()}/client/${pp.dir
		? `${pp.dir}/`
		: ''}${pp.name}!${(c.resourceHashes[rfn] || '000000').slice(0, 6)}${pp.ext}`;
}

type MaybeCB<A, B> = (it: Some<A>) => B;

export function isString(it: any): it is string {
	return typeof it == 'string';
}
export function isNumber(it: any): it is number {
	return typeof it == 'number';
}

export function isNone(it: any): it is None {
	return it === null || it === undefined;
}

export function isSome<T>(it: T): it is Some<T> {
	return it !== null && it !== undefined;
}

export function orNull<T extends Maybe<any>>(it: T): NonNullable<T> | null {
	return it === undefined ? null : it;
}
export function orVoid<T extends Maybe<any>>(it: T): NonNullable<T> | undefined {
	return it === null ? undefined : it;
}

export function maybe<IT, RT>(it: IT, action: MaybeCB<IT, RT>): RT | Extract<IT, null | undefined> {
	if (!isSome(it)) return it as Extract<IT, null | undefined>;
	return action(it);
}

export function apply<A, R>(it: A, action: (it: A) => R): R;
export function apply<A, B, R>(a: A, b: B, action: (a: A, b: B) => R): R;
export function apply<A, B, C, R>(a: A, b: B, c: C, action: (a: A, b: B, c: C) => R): R;
export function apply<A, B, C, D, R>(a: A, b: B, c: C, d: D, action: (a: A, b: B, c: C, d: D) => R): R;
export function apply(...args: any[]) {
	const action = args.pop();
	return action(...args);
}

export function maybePipe<A, B>(it: A, action: MaybeCB<A, B>): Maybe<B>;
export function maybePipe<A, B, C>(it: A, a: MaybeCB<A, B>, b: MaybeCB<B, C>): Maybe<C>;
export function maybePipe<A, B, C, D>(it: A, a: MaybeCB<A, B>, b: MaybeCB<B, C>, c: MaybeCB<C, D>): Maybe<D>;
export function maybePipe<A, B, C, D, E>(
	it: A,
	a: MaybeCB<A, B>,
	b: MaybeCB<B, C>,
	c: MaybeCB<C, D>,
	d: MaybeCB<D, E>,
): Maybe<E>;
export function maybePipe<A, B, C, D, E, F>(
	it: A,
	a: MaybeCB<A, B>,
	b: MaybeCB<B, C>,
	c: MaybeCB<C, D>,
	d: MaybeCB<D, E>,
	e: MaybeCB<E, F>,
): Maybe<F>;
export function maybePipe(it: any, ...actions: ((it: any) => any)[]) {
	while (it !== null && it !== undefined && actions.length) it = actions.shift()!(it);
	return it;
}

export function pick<T, K extends keyof T>(obj: T, ...keys: readonly (K | readonly K[])[]): Pick<T, K> {
	const out: any = {};
	for (const k of keys.flat(1) as K[]) if (hasOwnProperty(obj, k)) out[k] = obj[k];
	return out;
}

export function omit<T, K extends keyof T>(obj: T, ...keys: readonly (K | readonly K[])[]): Omit<T, K> {
	const out: any = {};
	const flat = keys.flat(1);
	for (const k in obj) if (!flat.find(m => m == k)) out[k] = obj[k];
	return out;
}

export function edit<T>(obj: T, keys: any[], value: any): T {
	const clone: any = Array.isArray(obj) ? obj.slice() : ({ ...obj });
	clone[keys[0]] = keys.length <= 1 ? value : edit(clone[keys[0]], keys.slice(1), value);
	return clone;
}

export function findMatch<T>(array: T[], pattern: Partial<T>) {
	const entries = Object.entries(pattern) as [keyof T, any][];
	return array.find(it => entries.every(([k, v]) => it[k] === v));
}

export function updateMatch<T>(array: T[], pattern: Partial<T>, update: Partial<T>) {
	const entries = Object.entries(pattern) as [keyof T, any][];
	return array.map(it => entries.every(([k, v]) => it[k] === v) ? { ...it, ...update } : it);
}

export function replaceMatch<T>(array: T[], pattern: Partial<T>, value: T) {
	const entries = Object.entries(pattern) as [keyof T, any][];
	return array.map(it => entries.every(([k, v]) => it[k] === v) ? value : it);
}

export function makeIndex<T extends { [P in K]: I }, K extends keyof any = 'id', I extends keyof any = T[K]>(
	arr: T[],
	key: K = 'id' as K,
) {
	return Object.fromEntries(arr.map(it => [it[key], it])) as Index<T, K>;
}

export function indexInto<T, K extends keyof T>(it: T) {
	return (key: K) => it[key];
}
export function ensureIndex<T, K extends keyof T>(it: T) {
	return (key: K) => {
		const val = it[key];
		if (!isSome(val)) throw utilError(ensureIndex, `Unexpected ${it === null ? 'null' : 'undefined'} at index`);
		return val;
	};
}

export function lazy<T, A extends any[] = []>(compute: (...args: A) => T): (...args: A) => T {
	let computed = false;
	let value: Maybe<T> = null;
	return (...args: A) => {
		if (!computed) {
			value = compute(...args);
			computed = true;
		}
		return value as T;
	};
}

const base36 = Array(36).fill(0).map(
	(_, i) => String.fromCharCode(i >= 10 ? i - 10 + 'a'.charCodeAt(0) : i + '0'.charCodeAt(0)),
);
export const getRandomNumbers = typeof window !== 'undefined' ? function generateIdImpl(length: number) {
	const arr = new Uint8Array(length);
	crypto.getRandomValues(arr);
	return Array.from(arr);
} : function generateIdImpl(length: number) {
	return Array.from(randomBytes(length));
};

export function generateId(length = 8) {
	return getRandomNumbers(length).map((n: number) => base36[n % 36]).join('');
}

export function makeSlug(s: string, max = 25) {
	const parts = (s)
		.replace(/'s\b/g, 's')
		.replace(/_/g, '-')
		.replace(/\W+/g, '-')
		.toLowerCase()
		.split('-')
		.filter(s => !!s);
	while (parts.join('-').length > max && parts.length > 1) parts.pop();
	return parts.join('-').slice(0, max) || '-';
}

export function formatPrice(price: Decimal, currId: Maybe<string> = 'USD') {
	return num(price).toLocaleString('en-US', {
		style: 'currency',
		currency: currId ?? 'USD',
		currencyDisplay: 'symbol',
	});
}

/**
 * Returns currId unchanged if set and *not* USD, otherwise null.
 * I.e. "does this cart/order need conversion, and if yes, ..."
 * @param currId
 */
export function makeFormatPriceForCurrency(currId: Maybe<CurrencyId>) {
	return (price: Decimal) => formatPrice(price, currId);
}

export function getShipmentProviderName(provider: Maybe<string>) {
	if (provider?.match(/^dhl/i)) return 'DHL';
	if (provider?.match(/^ups/i)) return 'UPS';
	if (provider?.match(/^fedex/i)) return 'FedEx';
	if (provider?.match(/^usps/i)) return 'USPS';
	return provider?.toUpperCase();
}

export function getTrackingLink(provider: string | null | undefined, tracking: string) {
	if (tracking.match(/^https?:\/\//)) return tracking;
	if (provider?.match(/^dhl/i)) return `https://www.dhl.com/en/express/tracking.html?AWB=${tracking}&brand=DHL`;
	if (provider?.match(/^ups/i)) return `https://wwwapps.ups.com/WebTracking/processInputRequest?TypeOfInquiryNumber=T&InquiryNumber1=${tracking}`;
	if (!provider || provider.match(/^usps/i)) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tracking}`;
	return null;
}

export const within = (min: number, n: number, max: number) => Math.max(min, Math.min(max, n));

export const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function assertSome<T>(it: T): asserts it is NonNullable<T> {
	if (it === undefined || it === null) {
		throw utilError(assertSome, `Unexpected ${it === null ? 'null' : 'undefined'}`);
	}
}
export function ensure<T>(it: T): NonNullable<T> {
	if (it === undefined || it === null) {
		throw utilError(ensure, `Unexpected ${it === null ? 'null' : 'undefined'}`);
	}
	return it;
}

export const hasOwnProperty = <T, K extends PropertyKey> (
	it: T,
	key: K,
): key is K & keyof T => it !== null && it !== undefined && Object.prototype.hasOwnProperty.call(it, key);

export const objectKeys = <T extends object> (it: T) => Object.keys(it) as (keyof T)[];

export type ObjectEntry<T> = Some<({
	[K in keyof T]: [K, T[K]]
})[keyof T]>;

/** This isn't logically correct, as any object *may* have additional properties. But practically, that'd probably be a bug anyway. */
export const objectEntries = <const T extends { [s: string]: any; }> (it: T) => Object.entries(it) as ObjectEntry<T>[];

export const parseURL = (str: string) => url.parse(str, true) as UrlWithParsedQuery;

export const isQuerySafeString = (v: string) => !!(
	v.match(/^\w[\w+:-]+$/)
	&& !v.match(/^[+-]?\d+\.?\d*$/)
	&& !['true', 'false', 'null'].includes(v)
);

const periods = ['week', 'month', 'quarter', 'year'] as const;
const dayPeriods = [7, 30, 90, 365] as const;
type Period = typeof periods[number];
export type DateRangeId =
	'today'
	| 'tomorrow'
	| 'yesterday'
	| `this${Capitalize<Period>}`
	| `prev${Capitalize<Period>}`
	| `last${typeof dayPeriods[number]}days`
	| 'all';
export type DateRangeFunction = () => DateRange;
export type DateRange = {
	id?: Maybe<string>;
	name?: Maybe<string>;
	compute?: DateRangeFunction;
	start?: Maybe<Dayjs>;
	end?: Maybe<Dayjs>;
};

const today = () => dayjs().startOf('day');
export const dateRanges = {
	
	today: {
		name: 'Today',
		compute() {
			return { ...this, start: today(), end: today().endOf('day') };
		},
	},
	yesterday: {
		name: 'Yesterday',
		compute() {
			return { ...this, start: today().subtract(1, 'day'), end: today().subtract(1, 'day').endOf('day') };
		},
	},
	tomorrow: {
		name: 'Tomorrow',
		compute() {
			return { ...this, start: today().add(1, 'day'), end: today().add(1, 'day').endOf('day') };
		},
	},
	
	...Object.fromEntries(periods.map(p => [
		`this${capitalize(p)}` as const, {
			name: `This ${capitalize(p)}`,
			compute() {
				return { ...this, start: dayjs().startOf(p), end: dayjs().endOf(p) };
			},
		},
	] as const)),
	
	...Object.fromEntries(periods.map(p => [
		`prev${capitalize(p)}` as const, {
			name: `Previous ${capitalize(p)}`,
			compute: () => apply(p == 'quarter' ? dayjs().startOf(p).subtract(3, 'month') : dayjs().startOf(p).subtract(
				1,
				p,
			), start => ({
				start,
				end: start.endOf(p),
			})),
		},
	] as const)),
	
	...Object.fromEntries(dayPeriods.map(p => [
		`last${p}days` as const, {
			name: `Last ${p} Days`,
			compute() {
				return { ...this, start: today().subtract(p, 'day'), end: null };
			},
		},
	] as const)),
	
	all: {
		name: 'All',
		compute() {
			return { ...this, start: null, end: null };
		},
	},
	
} as Record<DateRangeId, DateRange & { compute: DateRangeFunction }>;
for (const key in dateRanges) dateRanges[key as DateRangeId].id = key;

export const dateRangeToParams = (range: DateRange) => ({
	startDate: range.start?.format() as Maybe<string>,
	endDate: range.end?.format() as Maybe<string>,
});

export const isNumeric = (val: Maybe<number | string>) => isSome(val) && (
	(typeof val == 'number' && isFinite(val))
	|| (val != '' && isFinite(Number(val)))
);

/**
 * This function always returns string, but the return type is set to Decimal to enforce no expectations.
 */
export const decimal = (n: string | number, places = 2): Decimal => {
	let val = Number(n);
	if (!isFinite(val) || n === '') {
		throw utilError(decimal, `NaN passed to decimal(${n})`);
	}
	const mul = Math.pow(10, places);
	val = Math.sign(val) * (Math.round(Math.abs(val) * mul) / mul);
	if (Math.abs(val) < Math.pow(0.1, places) * 0.5) val = 0; // turn negative zero and other weird stuff into just zero
	return val.toFixed(places);
};

/**
 * Converts input to a number, rounded to 2 decimal places by default. Throws on NaN and other weird values.
 */
export const num = (n: Decimal, places = 2): number => {
	const val = Number(n);
	if (!isFinite(val) || n === '') {
		throw utilError(num, `NaN passed to num(${n})`);
	}
	const mul = Math.pow(10, places);
	return Math.sign(val) * (Math.round(Math.abs(val) * mul) / mul);
};

/**
 * Checks whether value is an element of the list and asserts that the value is of the element-type for that list.
 * E.g. if (isIn(value, ['a', 'b'] as const)) then value: 'a' | 'b'
 */
export function isIn<V, const T extends readonly (V | string | number)[]>(value: V, list: T): value is V & T[number] {
	return list.includes(value);
}

/**
 * Opposite of isIn, asserts that value is *not* one of listed..
 */
export function notIn<V, const T extends readonly (V | string | number)[]>(value: V, list: T): value is V extends T[number] ? never : V {
	return !list.includes(value);
}

export function ensureIn<V, const T extends readonly (V | string | number)[]>(value: V, list: T): V & T[number] {
	if (!isIn(value, list)) throw utilError(ensureIn, `Unexpected value: ${value}`);
	return value;
}

export type Mutable<T> = { -readonly [P in keyof T]: T[P] };
export type DeepMutable<T> = { -readonly [P in keyof T]: T[P] extends object ? DeepMutable<T[P]> : T[P] };

/**
 * Remove Readonly<T> effects from a value.
 * Does not actually *do* anything at runtime.
 */
export function asMutable<T>(value: T) {
	return value as Mutable<T>;
}

/**
 * Deep clone a value and remove any Readonly<T> effects.
 */
export function cloneAsMutable<T>(value: T) {
	return structuredClone(value) as Mutable<T>;
}

/**
 * If passed an object, returns its `.id`. Otherwise, assumes the passed argument is the id already and returns that.
 * If passed None, returns None.
 */
export function idOf<T extends Maybe<Identified | string | number>>(it: T) {
	return (it && typeof it == 'object' ? it.id : it) as (
		T extends None
			? Maybe<IdOf<Some<T>>>
			: IdOf<Some<T>>
	);
}

export const isFormError = (e: UserError): e is FormError => hasOwnProperty(e, 'field');
export const isCartItemError = (e: UserError): e is CartItemError => hasOwnProperty(e, 'item');

export const preventNavigatingAway = (msg: string) => window.onbeforeunload = _ => msg;
export const allowNavigatingAway = () => window.onbeforeunload = null;

export const getClasses = (classes: Record<string, boolean>): string => {
	return Object.keys(classes).filter(className => classes[className]).join(' ');
};

export function trace<T>(tag: any, tag2: any, it: T): T;
export function trace<T>(tag: any, it: T): T;
export function trace<T>(it: T): T;
export function trace<T>(...args: any[]) {
	console.log(...args);
	return args[args.length - 1];
}

export function logRejection(e: Error) {
	console.error(e);
	// @ts-ignore
	if (typeof captureException != 'undefined') captureException(e);
}
